import numeral from "numeral";
import { GoalState, GoalStatus } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

export type GoalProgressPillProps = {
  progress: number;
  status: GoalStatus;
  state: GoalState;
  isStale: boolean;
  className?: string;
  hideTooltip?: boolean;
};

// Pick the progress ring icon variant based on the goal's progress / state.
// 5 buckets, matching the icon set.
const getProgressIconType = (
  progress: number,
  isClosed: boolean,
): TopicflowIconType => {
  if (isClosed || progress >= 100) return "progress100";
  if (progress >= 60) return "progress75";
  if (progress >= 40) return "progress50";
  if (progress > 0) return "progress25";
  return "progress0";
};

// Pick the chip color pair (background + icon/text color) from the goal's
// status. `closed` always wins regardless of status.
const getChipColors = (
  status: GoalStatus,
  isClosed: boolean,
): { bg: string; text: string } => {
  if (isClosed) return { bg: "bg-emerald-100", text: "text-emerald-500" };
  if (status === GoalStatus.OnTrack)
    return { bg: "bg-blue-100", text: "text-blue-500" };
  if (status === GoalStatus.AtRisk)
    return { bg: "bg-amber-100", text: "text-amber-600" };
  if (status === GoalStatus.OffTrack)
    return { bg: "bg-red-100", text: "text-red-600" };
  // GoalStatus.None / undefined
  return { bg: "bg-gray-100", text: "text-gray-400" };
};

const GoalProgressPill = ({
  status,
  state,
  isStale,
  progress,
  className = "",
  hideTooltip = false,
}: GoalProgressPillProps) => {
  const isClosed = state === GoalState.Closed;
  const label = useLabel();

  const staleText = isStale
    ? " has not been updated for more than 2 weeks and"
    : "";
  let tooltipText = `This ${label("goal")}${staleText} has no status.`;
  if (isClosed) {
    tooltipText = `${label("goal", { capitalize: true })} is closed.`;
  }
  if (status === GoalStatus.OnTrack) {
    tooltipText = `${label("goal")}${staleText} is on track.`;
  }
  if (status === GoalStatus.OffTrack) {
    tooltipText = `${label("goal")}${staleText} is off track.`;
  }
  if (status === GoalStatus.AtRisk) {
    tooltipText = `${label("goal")}${staleText} is at risk.`;
  }

  const { bg, text } = getChipColors(status, isClosed);
  const iconType = getProgressIconType(progress, isClosed);

  return (
    <Tooltip text={hideTooltip ? null : tooltipText}>
      <div
        className={classNames(
          "flex justify-center items-center min-w-12 gap-1 px-0.5 py-0.5 text-xs rounded-full shrink-0",
          bg,
          text,
          className,
        )}
      >
        <TopicflowIcon type={iconType} className="h-3 w-3" />
        {numeral(Math.floor(progress)).format("0,0")}%
      </div>
    </Tooltip>
  );
};

export default GoalProgressPill;
